(() => {
  const BASE_SIZE = 24;

  // Shopify always paginates 24. Carry displaced products onto subsequent pages.
  const planPage = (productCount, promoPage, promoSize, requestedPage) => {
    const total = Math.max(0, Math.min(25000, productCount));
    const reduction = total > (promoPage - 1) * BASE_SIZE ? BASE_SIZE - promoSize : 0;
    const pages = Math.max(1, Math.ceil((total + reduction) / BASE_SIZE));
    const page = Math.max(1, Math.min(pages, requestedPage));
    const start = (page - 1) * BASE_SIZE - (page > promoPage ? reduction : 0);
    const count = Math.max(0, Math.min(page === promoPage ? promoSize : BASE_SIZE, total - start));
    const sources = [];
    for (let index = start; index < start + count;) {
      const nativePage = Math.floor(index / BASE_SIZE) + 1;
      const offset = index % BASE_SIZE;
      const length = Math.min(BASE_SIZE - offset, start + count - index);
      sources.push({ page: nativePage, offset, count: length });
      index += length;
    }
    return { page, pages, start, count, sources };
  };

  const pageUrl = (url, page, sectionId) => {
    const result = new URL(url);
    result.hash = '';
    result.searchParams.delete('section_id');
    result.searchParams.delete('plp_page');
    result.searchParams.set('page', String(page));
    if (sectionId) result.searchParams.set('section_id', sectionId);
    return result;
  };

  const requestedPageNumber = (url) => {
    const params = new URL(url).searchParams;
    const nativePage = Math.max(1, Number.parseInt(params.get('page'), 10) || 1);
    // Sorting clears `page`; an old logical-page marker must not survive it.
    return params.has('page') ? Math.max(nativePage, Number.parseInt(params.get('plp_page'), 10) || 1) : 1;
  };

  const logicalPageUrl = (url, page, total) => {
    const nativeLast = Math.max(1, Math.ceil(Math.min(25000, total) / BASE_SIZE));
    const result = pageUrl(url, Math.min(page, nativeLast));
    // An extra final page must still resolve to a valid Shopify URL on reload
    // or without JavaScript, rather than requesting a nonexistent native page.
    if (page > nativeLast) result.searchParams.set('plp_page', String(page));
    return result;
  };

  const pageNumbers = (current, last) => {
    const numbers = [];
    let previous = 0;
    for (let page = 1; page <= last; page++) {
      if (page !== 1 && page !== last && Math.abs(page - current) > 2) continue;
      if (page > previous + 1) numbers.push('…');
      numbers.push(page);
      previous = page;
    }
    return numbers;
  };

  // Test the actual production planner without a browser.
  if (typeof module !== 'undefined' && module.exports) {
    module.exports = { planPage, pageUrl, logicalPageUrl, requestedPageNumber, pageNumbers };
    return;
  }
  if (customElements.get('collection-promo-pagination')) return;

  const states = new WeakMap();
  const checkAbort = (signal) => {
    if (signal?.aborted) throw new DOMException('Aborted', 'AbortError');
  };
  const viewportPromoSize = (controller) => {
    if (controller.dataset.gridMode !== 'medium') return BASE_SIZE;
    if (window.matchMedia('(min-width: 1000px)').matches) return 23;
    if (window.matchMedia('(min-width: 700px)').matches) return 22;
    return BASE_SIZE;
  };

  // Template contents are inert: extra fetched cards never connect, initialize
  // their carousels, or load images. Only the selected cards are adopted.
  const parseSection = (state, html) => {
    const template = document.createElement('template');
    template.innerHTML = html;
    const section = template.content.querySelector(`#${CSS.escape(state.section.id)}`);
    const controller = section?.querySelector('collection-promo-pagination');
    if (controller?.dataset.collectionId !== state.collectionId ||
        Number(controller.dataset.pageSize) !== BASE_SIZE) {
      throw new Error('Unexpected collection pagination response.');
    }
    return section;
  };

  const nativeSection = async (state, url, page, signal) => {
    checkAbort(signal);
    const requestUrl = pageUrl(url, page, state.sectionId).href;
    let html = state.cache.get(requestUrl);
    if (!html) {
      const response = await fetch(requestUrl, { signal });
      if (!response.ok) throw new Error('Unable to load collection products.');
      html = await response.text();
    }
    checkAbort(signal);
    const section = parseSection(state, html);
    if (Number(section.querySelector('[data-plp-native-page]')?.dataset.plpNativePage) !== page) {
      throw new Error('Unexpected native collection page.');
    }
    // Keep at most two unmodified native HTML responses, not a growing gallery.
    state.cache.delete(requestUrl);
    state.cache.set(requestUrl, html);
    if (state.cache.size > 2) state.cache.delete(state.cache.keys().next().value);
    return section;
  };

  const updatePagination = (section, controller, plan, url) => {
    const main = section.querySelector('.collection__main');
    if (!main) return;
    main.querySelector('.pagination')?.remove();
    if (plan.pages <= 1) return;

    const nav = document.createElement('nav');
    nav.className = 'pagination';
    nav.setAttribute('role', 'navigation');
    nav.setAttribute('aria-label', controller.dataset.paginationLabel);
    const item = (page, text, relation) => {
      const current = page === plan.page && !relation;
      const tag = page && !current ? 'a' : relation ? 'p' : 'span';
      const element = document.createElement(tag, tag === 'a' ? { is: 'facet-link' } : undefined);
      element.className = 'pagination__link';
      element.textContent = text;
      if (page && !current) {
        element.setAttribute('is', 'facet-link');
        element.setAttribute('href', logicalPageUrl(url, page, Number(controller.dataset.productCount)).href);
        element.setAttribute('aria-label', controller.dataset.pageLabel.replace('__PAGE__', page));
        if (relation) element.setAttribute('rel', relation);
      } else {
        element.classList.add(relation ? 'pagination__link__disabled' : 'pagination__link--disabled');
        if (current) element.setAttribute('aria-current', 'page');
      }
      return element;
    };
    nav.append(item(plan.page > 1 ? plan.page - 1 : null, controller.dataset.prevLabel, 'prev'));
    const numbers = document.createElement('div');
    numbers.className = 'pagination--numbers';
    for (const page of pageNumbers(plan.page, plan.pages)) {
      numbers.append(item(typeof page === 'number' ? page : null, String(page)));
    }
    nav.append(numbers, item(plan.page < plan.pages ? plan.page + 1 : null, controller.dataset.nextLabel, 'next'));
    main.append(nav);
  };

  const compose = async (state, url, signal) => {
    const requestedPage = requestedPageNumber(url);
    const context = pageUrl(url, 1).href;
    const hintedCount = state.context === context ? state.productCount : null;
    // New filters are counted on native page 1 before requesting a later page.
    // Known collections never request a native page past Shopify's last page.
    const firstNativePage = hintedCount === null ? 1 : Math.min(requestedPage,
      Math.max(1, Math.ceil(Math.min(25000, hintedCount) / BASE_SIZE)));
    let section = await nativeSection(state, url, firstNativePage, signal);
    let controller = section.querySelector('collection-promo-pagination');
    const total = Number(controller.dataset.productCount);
    const promoPage = Number(controller.dataset.promoPage);
    const plan = planPage(total, promoPage, state.promoSize, requestedPage);
    const logicalUrl = logicalPageUrl(url, plan.page, total);
    if (!total) return { section, url: logicalUrl };

    // Promos follow logical pages, not the native sources of displaced products.
    // An additional final page reuses the last native page.
    const basePage = Math.min(plan.page, Math.ceil(Math.min(25000, total) / BASE_SIZE));
    if (basePage !== firstNativePage) {
      section = await nativeSection(state, url, basePage, signal);
      controller = section.querySelector('collection-promo-pagination');
    }
    const grid = section.querySelector(`#${CSS.escape(`product-list-${state.sectionId}`)}`);
    if (!grid) throw new Error('Collection grid is missing.');

    const selected = [];
    for (const source of plan.sources) {
      const sourceSection = source.page === basePage ? section : await nativeSection(state, url, source.page, signal);
      if (Number(sourceSection.querySelector('collection-promo-pagination').dataset.productCount) !== total) {
        state.cache.clear();
        throw new Error('Collection changed while loading. Please retry.');
      }
      const sourceGrid = sourceSection.querySelector(`#${CSS.escape(`product-list-${state.sectionId}`)}`);
      const cards = Array.from(sourceGrid.children).filter((node) => node.matches('product-card'));
      const slice = cards.slice(source.offset, source.offset + source.count);
      if (slice.length !== source.count) throw new Error('Collection response is incomplete.');
      selected.push(...slice.map((card) => card.cloneNode(true)));
    }
    if (new Set(selected.map((card) => card.getAttribute('handle'))).size !== plan.count) {
      throw new Error('Duplicate collection products in response.');
    }
    checkAbort(signal);
    Array.from(grid.children).filter((node) => node.matches('product-card')).forEach((card) => card.remove());
    // Preserve promo CSS/markup; remove only promos belonging to another page.
    grid.querySelectorAll('[data-content-block]').forEach((block) => {
      if (Number(block.dataset.contentBlockPage) !== plan.page) block.remove();
    });
    selected.forEach((card, index) => {
      const primary = card.querySelector('.product-card__image--primary');
      primary?.setAttribute('loading', index < Number(controller.dataset.firstRowCount) ? 'eager' : 'lazy');
    });
    grid.append(...selected);
    controller.dataset.visiblePage = String(plan.page);
    controller.dataset.visibleCount = String(plan.count);
    updatePagination(section, controller, plan, url);
    state.context = context;
    state.productCount = total;
    return { section, url: logicalUrl };
  };

  const initialize = async (state) => {
    if (state.promoSize === BASE_SIZE) return;
    const url = new URL(window.location.href);
    const page = requestedPageNumber(url);
    // The full document supplies the initial native page, with no page-1 fetch.
    const nativePage = Number(state.section.querySelector('[data-plp-native-page]')?.dataset.plpNativePage);
    state.cache.set(pageUrl(url, nativePage, state.sectionId).href, state.section.outerHTML);
    try {
      const result = await compose(state, url);
      if (!state.section.isConnected || window.location.href !== url.href) return;
      state.section.replaceChildren(...document.importNode(result.section, true).childNodes);
      if (requestedPageNumber(result.url) !== page) {
        history.replaceState(history.state, '', result.url.href);
      }
    } catch (error) {
      state.promoSize = BASE_SIZE;
      state.cache.clear();
      console.warn('[collection-pagination] Keeping native 24-product pagination.', error);
    }
  };

  window.collectionPromoPagination = {
    async render(section, url, signal) {
      const state = states.get(section);
      if (!state || !section.querySelector('collection-promo-pagination')) return null;
      await state.ready;
      checkAbort(signal);
      if (state.promoSize === BASE_SIZE) return null;
      return compose(state, url, signal);
    }
  };

  class CollectionPromoPagination extends HTMLElement {
    connectedCallback() {
      const section = this.closest('.shopify-section');
      if (!section || states.has(section)) return;
      const state = {
        section, sectionId: this.dataset.sectionId, collectionId: this.dataset.collectionId,
        promoSize: viewportPromoSize(this), cache: new Map(),
        context: pageUrl(window.location.href, 1).href,
        productCount: Number(this.dataset.productCount)
      };
      // Select once per document; resizing never changes product boundaries.
      states.set(section, state);
      state.ready = initialize(state);
    }
  }
  customElements.define('collection-promo-pagination', CollectionPromoPagination);
})();
